import { daysBetween, todayStr } from "./format";

/**
 * When each client call is due (Asana 1219802379957808):
 *  - CC  – Confirmation call: from 5 days before the first service date;
 *  - MIP – Move in Progress: the day of the move;
 *  - APF – After pickup (long distance): the day after pickup, to hear how it went;
 *  - ETA – ETA update (long distance): every Tuesday and Thursday until delivery;
 *  - ADF – After delivery: the day after delivery (Post Move, clients to call).
 * Long distance logs MIP and APF as one call ("MIP / APF completed"), so the
 * in-progress call clears both.
 */

export const CC_DAYS_BEFORE = 5;

/** Before the move: the queues a job waits in until its confirmation call. */
const BEFORE_MOVE = [
  "daily-assign",
  "weekly-assign",
  "intro-done",
  "ready-to-confirm",
  "confirmed-update",
  "ld-to-confirm",
  "ld-confirmed-weekly",
];

/** Long distance statuses while the shipment is on its way to the client. */
const LD_ON_THE_WAY = ["ld-in-transit", "ld-window-ending", "ld-warehouse-received", "ld-sit-confirmed", "ld-driver-delivery"];

const ETA_DAYS = [2, 4]; // Tuesday, Thursday

const weekday = (date) => new Date(date + "T12:00:00").getDay();

const firstServiceDate = (job) => [job.packingDate, job.loadingDate, job.moveDate].filter(Boolean).sort()[0] || "";

const days = (n) => `${n} ${n === 1 ? "day" : "days"}`;

/** The calls due today (or overdue) on this job: [{code, label, overdue}]. */
export const callsDue = (job, today = todayStr()) => {
  if (!job || job.postMoveClosed) return [];
  const out = [];
  const ld = job.moveType === "ld";
  const status = String(job.status || "");

  const start = firstServiceDate(job);
  const toGo = start ? daysBetween(today, start) : null;
  if (!job.confirmCallDone && BEFORE_MOVE.includes(status) && toGo !== null && toGo >= 0 && toGo <= CC_DAYS_BEFORE) {
    out.push({ code: "CC", label: toGo === 0 ? "CC due · moving today" : `CC due · move in ${days(toGo)}`, overdue: toGo <= 1 });
  }

  if (!job.inProgressCallDone && job.moveDate) {
    if (ld && job.moveDate < today && (status === "in-progress" || LD_ON_THE_WAY.includes(status))) {
      const late = daysBetween(job.moveDate, today) - 1;
      out.push({ code: "APF", label: late > 0 ? `APF overdue by ${days(late)}` : "APF due today", overdue: late > 0 });
    } else if (status === "in-progress") {
      const late = job.moveDate < today;
      out.push({ code: "MIP", label: late ? "MIP overdue" : "MIP due today", overdue: late });
    }
  }

  if (ld && LD_ON_THE_WAY.includes(status) && !job.driverDelivered && ETA_DAYS.includes(weekday(today))) {
    const toldToday = (job.etaLog || []).some((e) => e.kind === "customer" && String(e.date || "").slice(0, 10) === today);
    if (!toldToday) out.push({ code: "ETA", label: "ETA update due today", overdue: false });
  }

  if (status === "post-move-vip" && !job.postMoveCallDone) {
    out.push({ code: "ADF", label: "ADF due", overdue: false });
  }

  return out;
};

/** Every job with a call due today, overdue ones first: [{job, call}]. */
export const callsDueToday = (jobs, today = todayStr()) =>
  (jobs || [])
    .flatMap((job) => callsDue(job, today).map((call) => ({ job, call })))
    .sort((a, b) => Number(b.call.overdue) - Number(a.call.overdue) || a.call.code.localeCompare(b.call.code));
